import { useQuery } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Hourglass } from 'lucide-react';
import { profileQueryOptions } from '@/api/queries/useProfile';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Header } from '@/layout/header/Header';

const SETUP_PENDING_POLL_INTERVAL_MS = 15_000;

/**
 * Waiting screen for members of an org whose Ersteinrichtung is still
 * running (ADR 0011). Polls the profile until the org-manager finishes the
 * wizard, then forwards to the org home.
 */
export function SetupPendingPage() {
  const { t } = useTranslation();
  const { token } = useAuthStore();
  const slug = useAuthStore().slug();

  useDocumentTitle('initialSetup.pending.title');

  const { data: profile } = useQuery({
    ...profileQueryOptions,
    enabled: !!token,
    refetchInterval: SETUP_PENDING_POLL_INTERVAL_MS
  });

  // Not redundant with the route guard: the guard runs once on load, but this
  // screen polls and only re-renders here when the org-manager finishes the
  // wizard mid-wait — this is what forwards the waiting member to the org home.
  if (profile?.setupComplete) {
    return <Navigate to="/$slug" params={{ slug }} />;
  }

  return (
    // Renders outside the AppLayout chrome (the org is unusable until
    // Abschließen) but mounts its own Header like the wizard, so waiting
    // members can still switch orgs or log out.
    <div className="flex h-screen flex-col">
      <Header />
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto bg-surface-subtle p-8">
        <div className="max-w-md text-center">
          <Hourglass className="mx-auto h-12 w-12 animate-pulse text-info" />
          <h1 className="mt-6 text-2xl font-semibold text-foreground">
            {t('initialSetup.pending.title')}
          </h1>
          <p className="mt-3 text-sm text-fg-secondary">{t('initialSetup.pending.description')}</p>
        </div>
      </div>
    </div>
  );
}
